import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Editor } from "@monaco-editor/react";
import { useAlert } from "../../context/AlertContext";
import { Provider, ProviderRequest } from "../../types";
import api from "../../services/api";

interface ProviderFormProps {
  initialData?: Provider;
  isEdit?: boolean;
}

// List of supported providers
const supportedProviders = [
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic" },
  { value: "google:gemini", label: "Google (Gemini)" },
  { value: "mistral", label: "Mistral AI" },
  { value: "azure:openai", label: "Azure OpenAI" },
  { value: "cohere", label: "Cohere" },
  { value: "custom", label: "Custom Provider" },
];

// List of models for each provider
const providerModels: Record<
  string,
  Array<{ value: string; label: string }>
> = {
  openai: [
    { value: "gpt-4o", label: "GPT-4o" },
    { value: "gpt-4o-mini", label: "GPT-4o Mini" },
    { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
    { value: "gpt-4", label: "GPT-4" },
    { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
  ],
  anthropic: [
    { value: "claude-3-opus", label: "Claude 3 Opus" },
    { value: "claude-3-sonnet", label: "Claude 3 Sonnet" },
    { value: "claude-3-haiku", label: "Claude 3 Haiku" },
    { value: "claude-2", label: "Claude 2" },
  ],
  "google:gemini": [
    { value: "gemini-pro", label: "Gemini Pro" },
    { value: "gemini-ultra", label: "Gemini Ultra" },
  ],
  mistral: [
    { value: "mistral-large", label: "Mistral Large" },
    { value: "mistral-medium", label: "Mistral Medium" },
    { value: "mistral-small", label: "Mistral Small" },
  ],
  cohere: [
    { value: "command", label: "Command" },
    { value: "command-r", label: "Command-R" },
  ],
  "azure:openai": [
    { value: "gpt-4", label: "GPT-4" },
    { value: "gpt-35-turbo", label: "GPT-3.5 Turbo" },
  ],
  custom: [{ value: "custom", label: "Custom Model" }],
};

const ProviderForm: React.FC<ProviderFormProps> = ({
  initialData,
  isEdit = false,
}) => {
  const navigate = useNavigate();
  const alert = useAlert();
  const [loading, setLoading] = useState<boolean>(false);
  const [showApiKey, setShowApiKey] = useState<boolean>(false);

  // Parse initial provider and model
  const parseInitialProvider = (): { baseProvider: string; model: string } => {
    if (!initialData?.provider) {
      return { baseProvider: "openai", model: "gpt-4" };
    }

    const parts = initialData.provider.split(":");
    if (parts.length === 1) {
      return { baseProvider: parts[0], model: "" };
    }

    const baseProvider = parts[0];
    const model = parts.slice(1).join(":");

    return { baseProvider, model };
  };

  const { baseProvider, model } = parseInitialProvider();

  // Define validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    baseProvider: Yup.string().required("Provider is required"),
    model: Yup.string().required("Model is required"),
    // apiKey: Yup.string().when('baseProvider', {
    //   is: (val: string) => val !== 'custom',
    //   then: Yup.string().required('API key is required'),
    // }),
    // config: Yup.string().when('baseProvider', {
    //   is: 'custom',
    //   then: Yup.string().required('Configuration is required for custom providers'),
    // }),
  });

  // Initialize formik
  const formik = useFormik<{
    name: string;
    baseProvider: string;
    model: string;
    apiKey: string;
    config: string;
  }>({
    initialValues: {
      name: initialData?.name || "",
      baseProvider: baseProvider,
      model: model,
      apiKey: "", // We don't receive the API key from the backend for security
      config: initialData?.config
        ? JSON.stringify(initialData.config, null, 2)
        : "{\n  \n}",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Format provider string (e.g., "openai:gpt-4")
        const formattedProvider =
          values.baseProvider === "custom"
            ? "custom"
            : `${values.baseProvider}:${values.model}`;

        // Parse config JSON if present
        const configObject = values.config ? JSON.parse(values.config) : {};

        const requestData: ProviderRequest = {
          name: values.name,
          provider: formattedProvider,
          apiKey: values.apiKey,
          config: configObject,
        };

        if (isEdit && initialData?._id) {
          // If editing and no new API key provided, don't send it (to avoid overwriting)
          if (!values.apiKey) {
            delete requestData.apiKey;
          }

          await api.providers.update(initialData._id, requestData);
          alert.success("Provider updated successfully");
        } else {
          await api.providers.create(requestData);
          alert.success("Provider created successfully");
        }
        navigate("/providers");
      } catch (error) {
        console.error("Error saving provider:", error);
        if (error instanceof SyntaxError) {
          alert.error("Invalid JSON configuration format");
        } else {
          alert.error("Failed to save provider");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  // Toggle API key visibility
  const handleToggleApiKeyVisibility = () => {
    setShowApiKey(!showApiKey);
  };

  // Get available models for the selected provider
  const getAvailableModels = () => {
    return providerModels[formik.values.baseProvider] || [];
  };

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Provider Details
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <TextField
          fullWidth
          label="Provider Name"
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          variant="outlined"
          required
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <FormControl
            fullWidth
            error={
              formik.touched.baseProvider && Boolean(formik.errors.baseProvider)
            }
          >
            <InputLabel id="provider-label">Provider</InputLabel>
            <Select
              labelId="provider-label"
              name="baseProvider"
              value={formik.values.baseProvider}
              onChange={(e) => {
                formik.setFieldValue("baseProvider", e.target.value);
                // Reset model when provider changes
                formik.setFieldValue("model", "");
              }}
              onBlur={formik.handleBlur}
              label="Provider"
            >
              {supportedProviders.map((provider) => (
                <MenuItem key={provider.value} value={provider.value}>
                  {provider.label}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.baseProvider && formik.errors.baseProvider && (
              <FormHelperText>{formik.errors.baseProvider}</FormHelperText>
            )}
          </FormControl>

          <FormControl
            fullWidth
            error={formik.touched.model && Boolean(formik.errors.model)}
            disabled={formik.values.baseProvider === "custom"}
          >
            <InputLabel id="model-label">Model</InputLabel>
            <Select
              labelId="model-label"
              name="model"
              value={formik.values.model}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Model"
            >
              {getAvailableModels().map((model) => (
                <MenuItem key={model.value} value={model.value}>
                  {model.label}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.model && formik.errors.model && (
              <FormHelperText>{formik.errors.model}</FormHelperText>
            )}
          </FormControl>
        </Box>

        <TextField
          fullWidth
          label="API Key"
          name="apiKey"
          value={formik.values.apiKey}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.apiKey && Boolean(formik.errors.apiKey)}
          helperText={
            (formik.touched.apiKey && formik.errors.apiKey) ||
            (isEdit ? "Leave blank to keep existing API key" : "")
          }
          variant="outlined"
          type={showApiKey ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle api key visibility"
                  onClick={handleToggleApiKeyVisibility}
                  edge="end"
                >
                  {showApiKey ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Advanced Configuration
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Typography variant="body2" color="text.secondary" paragraph>
          {formik.values.baseProvider === "custom"
            ? "Configuration is required for custom providers."
            : "Optional configuration for additional provider-specific settings."}
        </Typography>

        <Box
          sx={{
            height: 300,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            mb: 2,
          }}
        >
          <Editor
            language="json"
            value={formik.values.config}
            onChange={(value) => formik.setFieldValue("config", value || "")}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              formatOnPaste: true,
              formatOnType: true,
            }}
          />
        </Box>
        {formik.touched.config && formik.errors.config && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {formik.errors.config}
          </Typography>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => navigate("/providers")}
        >
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          loading={loading}
        >
          {isEdit ? "Update Provider" : "Create Provider"}
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default ProviderForm;
